import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, FileText } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { useDashboard } from '@/context/DashboardContext';
import { useConfirm } from './ConfirmDialog';
import {
  Drawer, Block, FieldList, Badge, Loading, ContactLinks, Avatar, money, fdate, fdatetime, inputCls, btnGold, btnGhost,
} from '@/components/dashboard/kit';
import { TierBadge, TestBadge, PayBadge, STAGE, WEB_STATUS, TIER, eventTitle } from './people';

/*
  Ficha de una persona (Personas): todo lo que hay de ese email, desplegado y por secciones.
  Cuenta y membresía · test(s) con las respuestas en texto · formularios antiguos · inscripciones con lo
  que rellenó y su pago · pagos de membresía · documentos firmados · su red.
  Las acciones son las de siempre (membresía, rol, patrocinador, borrar, etapa del test, estado de una
  inscripción). Los importes solo llegan al superadmin (data.money).
*/
const MEMBERSHIPS = [['free', 'Free'], ['starter', 'Access'], ['basica', 'PRO'], ['premium', 'Premium']];
const memLabel = (v) => (MEMBERSHIPS.find(([k]) => k === v) || MEMBERSHIPS[0])[1];
// Acceso al panel de administración (campo role). El rol que se ve (Broker, PRO, Access, Free…) sale de la membresía.
const ROLE_LABEL = { member: 'No (socio)', admin: 'Sí (administrador)' };
const roleKey = (r) => (r === 'admin' ? 'admin' : 'member');  // «broker» era el valor antiguo de «member»
const ROLE_ACCESS = {
  admin: 'Acceso completo al panel de administración: personas, eventos, contenido, roles, membresías y borrado de usuarios. La Dirección (CDM) y las cifras de dinero siguen siendo solo del superadmin.',
  member: 'Sin acceso al panel de administración: usa la plataforma según su membresía.',
};
const BOOTCAMP_STATUS = [['approved', 'Confirmada'], ['pending', 'Pendiente de revisión'], ['rejected', 'Rechazada']];
const fold = (v) => String(v || '').trim().toLowerCase();
const digits = (v) => String(v || '').replace(/\D/g, '');
const card = 'rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-3';
const label = 'text-[11px] uppercase tracking-wider text-gray-400 font-semibold';

function renewalText(m) {
  if (!m?.plan) return null;
  if (m.mode === 'vitalicia') return `Vitalicia${m.lifetime_since ? ` desde el ${fdate(m.lifetime_since)}` : ''}`;
  if (!m.renews_at) return 'Anual';
  if (m.expired) return `Anual · caducó el ${fdate(m.renews_at)}`;
  if (m.renewal_cancelled) return `Anual · renovación cancelada, caduca el ${fdate(m.renews_at)}`;
  return `Anual · ${m.has_subscription ? 'renueva' : 'caduca'} el ${fdate(m.renews_at)}`;
}

export default function PersonDrawer({ personKey, onClose, onOpen, onChanged }) {
  const { user: me } = useDashboard();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, ask] = useConfirm();

  const load = useCallback(() => {
    if (!personKey) return;
    setError(null);
    dashboardApi.adminPerson(personKey).then(setData).catch((e) => setError(e.message || 'No se ha podido cargar la ficha'));
  }, [personKey]);
  useEffect(() => { setData(null); load(); }, [load]);

  const done = (msg) => { if (msg) toast.success(msg); load(); onChanged?.(); };
  const fail = (e, msg) => toast.error(e.message || msg);

  const acc = data?.account;
  const mem = data?.membership;

  // ── Membresía (cambio manual: no toca Stripe) ──
  const changeMembership = async (next) => {
    const cur = mem?.active_membership || 'free';
    if (cur === next) return;
    const to = memLabel(next);
    const ok = await ask({
      title: `Cambiar la membresía de ${acc.name}`,
      details: [
        { label: 'Ahora', value: `${mem.label}${mem.plan ? ` · ${renewalText(mem)}` : ''}` },
        { label: 'Pasará a', value: to },
        next === 'free'
          ? 'Pierde al momento el acceso de miembro (academia, oportunidades y herramientas).'
          : mem.has_subscription
            ? 'Tiene una suscripción anual en Stripe: sigue activa y se renovará como hasta ahora. Si hay que cancelarla o cambiar su precio, hazlo en Stripe.'
            : 'Es una asignación manual sin fecha de caducidad: contará como vitalicia.',
        next === 'premium' && cur !== 'premium' && 'Tendrá las herramientas de broker (red, actividad comercial, success kit…).',
        cur === 'premium' && next !== 'premium' && 'Pierde las herramientas de broker.',
        'No se crea ni se cancela ningún cobro.',
      ],
      confirmLabel: `Pasar a ${to}`,
      tone: next === 'free' ? 'danger' : 'gold',
    });
    if (!ok) return;
    try {
      await dashboardApi.adminChangeMembership(acc.id, next === 'free' ? null : next);
      done(`Membresía de ${acc.name}: ${to}`);
    } catch (e) { fail(e, 'No se ha podido cambiar la membresía'); }
  };

  // ── Rol ──
  const changeRole = async (role) => {
    if (roleKey(acc.role) === role) return;
    const ok = await ask({
      title: `Acceso al panel de admin de ${acc.name}`,
      details: [{ label: 'Ahora', value: ROLE_LABEL[roleKey(acc.role)] }, { label: 'Pasará a', value: ROLE_LABEL[role] },
        ROLE_ACCESS[role], 'El cambio se aplica en su próxima acción en la plataforma.'],
      confirmLabel: role === 'admin' ? 'Hacer administrador' : 'Quitar acceso de administrador',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await dashboardApi.adminChangeRole(acc.id, role);
      done(`Acceso al panel de admin de ${acc.name}: ${ROLE_LABEL[role]}`);
    } catch (e) { fail(e, 'No se ha podido cambiar el rol'); }
  };

  // ── Borrar usuario: primero se pregunta al servidor qué pasaría y se enseña tal cual ──
  const deleteUser = async () => {
    let p;
    try { p = (await dashboardApi.adminDeleteUserPreview(acc.id)).impact; } catch (e) { fail(e, 'No se ha podido preparar el borrado'); return; }
    const sp = p.new_sponsor;
    const contacts = p.leads + p.funnel_leads + p.referral_contacts;
    const ok = await ask({
      title: `Eliminar a ${acc.name}`,
      description: `${acc.email} · ${mem?.label || 'Free'}${mem?.plan ? `, ${renewalText(mem).toLowerCase()}` : ''}`,
      details: [
        p.referrals
          ? `Sus ${p.referrals === 1 ? 'referido directo pasa' : `${p.referrals} referidos directos pasan`} a ${sp?.id ? `${sp.name} (${sp.email})` : 'quedar sin patrocinador (no hay nadie activo por encima)'}.`
          : 'No tiene referidos directos.',
        contacts > 0 && `Sus leads y contactos (${contacts}) pasan a ${sp?.id ? sp.name : 'quedar sin broker'}.`,
        p.cdm_ids?.length > 0 && (p.is_cdm_broker
          ? `Es broker en el CDM (${p.cdm_ids.join(', ')}) con ${p.cdm_team} ${p.cdm_team === 1 ? 'socio' : 'socios'} en su red. Su ficha y sus comisiones se conservan (contabilidad); solo se quita el enlace con esta cuenta.`
          : `Tiene ficha en el CDM (${p.cdm_ids.join(', ')}): se conserva y solo se quita el enlace con esta cuenta.`),
        p.documents_kept > 0 && `Sus ${p.documents_kept} documentos firmados se conservan como prueba legal.`,
        p.purchases_kept > 0 && `Su historial de pagos (${p.purchases_kept}) se conserva.`,
        p.interests_deleted > 0 && `Se borran ${p.interests_deleted} solicitudes de oportunidades sin atender.`,
        p.interests_kept > 0 && `${p.interests_kept} solicitudes ya gestionadas se conservan marcadas como «Usuario eliminado».`,
        'Se borran su acceso, su progreso en la academia y sus notificaciones. Su test e inscripciones se quedan. No se puede deshacer.',
      ],
      confirmLabel: 'Eliminar usuario',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await dashboardApi.adminDeleteUser(acc.id);
      toast.success(`${acc.name} eliminado${p.referrals && sp?.id ? `; su equipo pasa a ${sp.name}` : ''}`);
      onChanged?.();
      load();
    } catch (e) { fail(e, 'No se ha podido eliminar'); }
  };

  const isMe = acc && me?.id === acc.id;
  const subtitle = data && (
    <span className="flex flex-wrap gap-1.5 mt-1">
      <TierBadge tier={data.tier} />
      {data.has_test && data.tests[0] && <TestBadge temperature={data.tests[0].temperature} score={data.tests[0].score} />}
      {data.has_test && !data.tests[0] && <Badge>Test antiguo</Badge>}
      {acc && acc.is_active === false && <Badge tone="bad">Cuenta desactivada</Badge>}
    </span>
  );

  return (
    <Drawer open={!!personKey} onClose={onClose} wide title={data?.name || 'Ficha'} subtitle={subtitle}>
      {dialog}
      {!data ? <Loading error={error} /> : (<>
        <Block title="Contacto">
          <FieldList items={[
            ['Email', data.email], ['Teléfono', data.phone],
            ['Quién le trajo', data.referrer && (data.referrer.key
              ? <button type="button" onClick={() => onOpen(data.referrer.key)} className="text-gold hover:underline text-left">{data.referrer.name}</button>
              : data.referrer.name)],
            ['Alta en la plataforma', acc ? fdate(acc.created_at) : 'Sin cuenta (solo test o inscripción)'],
          ]} />
          <ContactLinks email={data.email} phone={data.phone} />
        </Block>

        {acc && (
          <Block title="Cuenta y membresía">
            <FieldList items={[
              ['Membresía', mem.label], ['Modalidad', renewalText(mem)],
              ['Miembro desde', mem.since && fdate(mem.since)], ['Factura', mem.billing_entity],
              ['Asignada a mano por', mem.manual_by],
              ['Rol', (TIER[data.tier] || TIER.free)[0]], ['Código de invitación', acc.referral_code],
              ['Email verificado', acc.email_verified ? 'Sí' : 'No'],
              ['Nombre completo (KYC)', acc.kyc?.full_name], ['Documento', acc.kyc?.document_type], ['País', acc.kyc?.country],
              ['Ficha en el CDM', data.cdm.length ? data.cdm.map((c) => `${c.id} (${c.kind})`).join(', ') : null],
              ['Contacto oculto a su red', acc.network_contact_hidden ? 'Sí (T&C 14.6.4)' : null],
            ]} />
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={label}>Cambiar membresía</span>
                <select value={mem.active_membership || 'free'} onChange={(e) => changeMembership(e.target.value)}
                  className={`${inputCls} mt-1 w-full h-10`} aria-label="Cambiar membresía">
                  {MEMBERSHIPS.map(([k, l]) => <option key={k} value={k} className="bg-navy-panel">{l}</option>)}
                </select>
              </label>
              <label className="block">
                <span className={label}>Acceso al panel de admin</span>
                <select value={roleKey(acc.role)} onChange={(e) => changeRole(e.target.value)} disabled={isMe}
                  className={`${inputCls} mt-1 w-full h-10 disabled:opacity-50`} aria-label="Cambiar rol">
                  {Object.entries(ROLE_LABEL).map(([k, l]) => <option key={k} value={k} className="bg-navy-panel">{l}</option>)}
                </select>
              </label>
            </div>
            <SponsorPicker account={acc} sponsor={data.referrer?.id === acc.referred_by ? data.referrer : null} ask={ask}
              onSaved={(msg) => done(msg)} />
            {acc.role !== 'admin' && !isMe && (
              <button type="button" onClick={deleteUser} className={`${btnGhost} h-10 text-red-300 border-red-400/20 hover:bg-red-400/10`}>
                <Trash2 size={14} /> Eliminar usuario
              </button>
            )}
          </Block>
        )}

        {data.tests.length > 0 && (
          <Block title={data.tests.length === 1 ? 'Test de idoneidad' : `Tests de idoneidad (${data.tests.length})`}>
            {data.tests.map((t) => <TestCard key={t.id} t={t} person={data} onOpen={onOpen} onSaved={done} />)}
          </Block>
        )}

        {data.forms.length > 0 && (
          <Block title="Formularios de la web">
            {data.forms.map((f) => <FormCard key={f.id} f={f} onSaved={done} />)}
          </Block>
        )}

        {data.registrations.length > 0 && (
          <Block title="Inscripciones a eventos y bootcamps">
            {data.registrations.map((r) => <RegistrationCard key={r.id} r={r} money={data.money} ask={ask} onSaved={done} />)}
          </Block>
        )}

        {data.purchases.length > 0 && (
          <Block title="Pagos de membresía">
            <ul className="divide-y divide-white/[0.05] rounded-xl border border-white/[0.06]">
              {data.purchases.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                  <span className="min-w-0">
                    <span className="block text-white">{p.concept}</span>
                    <span className="block text-xs text-gray-400">{fdate(p.date)}{p.entity ? ` · ${p.entity}` : ''}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    {data.money && p.amount !== null && p.amount !== undefined && <span className="text-white tabular-nums">{money(p.amount, p.currency)}</span>}
                    <Badge tone={p.status === 'paid' ? 'good' : p.status === 'pending' ? 'warn' : 'neutral'}>{p.status_label}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {data.documents.length > 0 && (
          <Block title="Documentos firmados">
            <ul className="space-y-2">
              {data.documents.map((d) => (
                <li key={d.id} className="flex items-center gap-3 text-sm">
                  <FileText size={15} className="text-gold shrink-0" />
                  <span className="min-w-0 flex-1 text-white">{d.title || d.type_label}{d.version_label || d.version ? <span className="text-gray-400"> · versión {d.version_label || d.version}</span> : null}</span>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{d.status === 'signed' ? `Firmado el ${fdate(d.signed_at)}` : d.status}</span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {(data.referrals.length > 0 || data.referred_contacts.length > 0) && (
          <Block title="Su red">
            <PeopleList title="Con cuenta (le tienen como patrocinador)" rows={data.referrals} onOpen={onOpen} />
            <PeopleList title="Contactos que trajo y aún no tienen cuenta" rows={data.referred_contacts} onOpen={onOpen} />
          </Block>
        )}
      </>)}
    </Drawer>
  );
}

function PeopleList({ title, rows, onOpen }) {
  if (!rows.length) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 mb-2">{title} · {rows.length}</p>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.key}>
            <button type="button" onClick={() => onOpen(r.key)} className="w-full min-h-[44px] flex items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-white/[0.04]">
              <Avatar name={r.name} size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-white truncate">{r.name || r.email}</span>
                <span className="block text-xs text-gray-400 truncate">{r.email}</span>
              </span>
              {r.tier && <Badge tone={(TIER[r.tier] || TIER.free)[1]}>{(TIER[r.tier] || TIER.free)[0]}</Badge>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Patrocinador: buscar entre los usuarios con cuenta (en el servidor) y asignar o quitar
function SponsorPicker({ account, sponsor, ask, onSaved }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      dashboardApi.adminPeople({ kind: 'users', q, page_size: 8 })
        .then((d) => { if (alive) setRows(d.items.filter((r) => r.user_id && r.user_id !== account.id)); })
        .catch(() => { if (alive) setRows([]); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [open, q, account.id]);

  const assign = async (target) => {
    const ok = await ask({
      title: target ? `Asignar patrocinador a ${account.name}` : `Quitar el patrocinador de ${account.name}`,
      details: [{ label: 'Ahora', value: sponsor?.name || 'Sin patrocinador' }, { label: 'Pasará a', value: target?.name || 'Sin patrocinador' },
        'Cambia su red de patrocinio en la plataforma. Las comisiones del CDM no cambian.'],
      confirmLabel: target ? 'Asignar' : 'Quitar patrocinador',
      tone: 'gold',
    });
    if (!ok) return;
    try {
      await dashboardApi.adminAssignSponsor(account.id, target?.user_id || null);
      setOpen(false);
      onSaved(target ? `Patrocinador: ${target.name}` : 'Patrocinador quitado');
    } catch (e) { toast.error(e.message || 'No se ha podido cambiar el patrocinador'); }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-300">Patrocinador: <span className="text-white">{sponsor?.name || 'sin patrocinador'}</span></span>
        <button type="button" onClick={() => setOpen((o) => !o)} className={`${btnGhost} h-10`}>{open ? 'Cerrar' : 'Cambiar patrocinador'}</button>
      </div>
      {open && (
        <div className="rounded-xl border border-white/[0.06] p-3 space-y-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, email o teléfono" aria-label="Buscar patrocinador"
            className={`${inputCls} w-full h-10`} />
          <ul className="max-h-60 overflow-y-auto space-y-1">
            {account.referred_by && (
              <li><button type="button" onClick={() => assign(null)} className="w-full min-h-[40px] rounded-lg px-2 text-left text-sm text-gray-300 hover:bg-white/[0.04]">Quitar patrocinador</button></li>
            )}
            {rows.map((r) => (
              <li key={r.key}>
                <button type="button" onClick={() => assign(r)} className="w-full min-h-[44px] flex items-center gap-3 rounded-lg px-2 text-left hover:bg-white/[0.04]">
                  <Avatar name={r.name} size="sm" />
                  <span className="min-w-0 flex-1"><span className="block text-sm text-white truncate">{r.name}</span><span className="block text-xs text-gray-400 truncate">{r.email}</span></span>
                  <TierBadge tier={r.tier} />
                </button>
              </li>
            ))}
            {!rows.length && <li className="px-2 py-2 text-sm text-gray-400">Sin resultados</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// Un test: respuestas en texto, agenda, conversación con Freedy y seguimiento (etapa y notas)
function TestCard({ t, person, onOpen, onSaved }) {
  const [status, setStatus] = useState(t.status);
  const [notes, setNotes] = useState(t.notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = status !== t.status || notes !== (t.notes || '');
  const save = async () => {
    setSaving(true);
    try { await dashboardApi.adminUpdateFunnelLead(t.id, { status, notes }); onSaved('Seguimiento guardado'); } catch (e) { toast.error(e.message || 'No se ha podido guardar'); } finally { setSaving(false); }
  };
  return (
    <div className={card}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-white font-medium">{fdatetime(t.created_at)}</span>
        <TestBadge temperature={t.temperature} score={t.score} />
        {t.profile_label && <Badge>{t.profile_label}</Badge>}
        {t.disqualified && <Badge tone="bad">No cumple los requisitos</Badge>}
      </div>
      <FieldList cols={1} items={t.answers.map((a) => [a.question, a.answer || 'Sin respuesta'])} />
      <FieldList items={[
        // Nombre y teléfono del test solo si difieren de los de la ficha
        ['Nombre en el test', fold(t.name) !== fold(person.name) ? t.name : null],
        ['Teléfono en el test', digits(t.phone) !== digits(person.phone) ? t.phone : null],
        ['Preferencia de contacto', t.contact_preference],
        ['Quién le trajo', t.referrer && (t.referrer.key
          ? <button type="button" onClick={() => onOpen(t.referrer.key)} className="text-gold hover:underline text-left">{t.referrer.name}</button>
          : t.referrer.name)],
        ['Abrió la agenda', t.booking_clicked_at ? `${fdatetime(t.booking_clicked_at)}${t.booking_clicks > 1 ? ` (${t.booking_clicks} veces)` : ''}` : 'No'],
        ['Llamada agendada', t.booked_call_at && fdatetime(t.booked_call_at)],
        ['Origen', t.source],
      ]} />
      {t.conversation.length > 0 && (
        <details className="rounded-lg border border-white/[0.06] bg-navy-night/40">
          <summary className="cursor-pointer select-none px-3 min-h-[40px] flex items-center text-sm text-gold">Conversación con Freedy ({t.conversation.length} mensajes)</summary>
          <div className="px-3 pb-3 space-y-2">
            {t.conversation.map((m, i) => (
              <p key={i} className={`text-xs whitespace-pre-wrap rounded-lg px-2.5 py-1.5 max-w-[90%] ${m.role === 'user' ? 'ml-auto bg-gold/15 text-gold-light' : 'bg-white/[0.05] text-gray-300'}`}>{m.content}</p>
            ))}
          </div>
        </details>
      )}
      <div className="grid gap-2 sm:grid-cols-[200px_1fr]">
        <label className="block">
          <span className={label}>Etapa</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} mt-1 w-full h-10`} aria-label="Etapa del test">
            {Object.entries(STAGE).map(([k, l]) => <option key={k} value={k} className="bg-navy-panel">{l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={label}>Notas</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Apunta el resultado de la llamada…"
            className={`${inputCls} mt-1 w-full resize-y`} />
        </label>
      </div>
      {dirty && <button type="button" onClick={save} disabled={saving} className={`${btnGold} h-10`}>{saving ? 'Guardando…' : 'Guardar seguimiento'}</button>}
    </div>
  );
}

// Formulario antiguo de la web (antes del test con Freedy) o mensajes del formulario de contacto
function FormCard({ f, onSaved }) {
  const [status, setStatus] = useState(f.status);
  const [notes, setNotes] = useState(f.notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = status !== f.status || notes !== (f.notes || '');
  const save = async () => {
    setSaving(true);
    try { await dashboardApi.updateLead(f.id, { status, notes }); onSaved('Seguimiento guardado'); } catch (e) { toast.error(e.message || 'No se ha podido guardar'); } finally { setSaving(false); }
  };
  return (
    <div className={card}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-white font-medium">{fdatetime(f.created_at)}</span>
        {f.source && <Badge>{f.source}</Badge>}
        <Badge tone="info">{f.status_label}</Badge>
      </div>
      {f.answers.length > 0 && <FieldList cols={1} items={f.answers.map((a) => [a.question, a.answer])} />}
      {f.messages.length > 0 && (
        <div className="space-y-1.5">
          <p className={label}>Mensajes</p>
          {f.messages.map((m, i) => <p key={i} className="text-sm text-gray-300 whitespace-pre-wrap"><span className="text-xs text-gray-500">{fdatetime(m.at)} · </span>{m.message}</p>)}
        </div>
      )}
      {f.editable && (<>
        <div className="grid gap-2 sm:grid-cols-[200px_1fr]">
          <label className="block">
            <span className={label}>Estado</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} mt-1 w-full h-10`} aria-label="Estado del formulario">
              {Object.entries(WEB_STATUS).map(([k, l]) => <option key={k} value={k} className="bg-navy-panel">{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className={label}>Notas</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${inputCls} mt-1 w-full resize-y`} />
          </label>
        </div>
        {dirty && <button type="button" onClick={save} disabled={saving} className={`${btnGold} h-10`}>{saving ? 'Guardando…' : 'Guardar seguimiento'}</button>}
      </>)}
    </div>
  );
}

// Una inscripción: lo que rellenó, su pago y las acciones que existen (estado del bootcamp, pago del cóctel)
function RegistrationCard({ r, money: showMoney, ask, onSaved }) {
  const [notes, setNotes] = useState(r.admin_notes || '');
  const setBootcampStatus = async (status) => {
    if (status === r.status) return;
    try { await dashboardApi.bootcampUpdateRegistration(r.id, { status }); onSaved('Inscripción actualizada'); } catch (e) { toast.error(e.message || 'No se ha podido actualizar'); }
  };
  const saveNotes = async () => {
    if (notes === (r.admin_notes || '')) return;
    try { await dashboardApi.bootcampUpdateRegistration(r.id, { admin_notes: notes }); onSaved('Nota guardada'); } catch (e) { toast.error(e.message || 'No se ha podido guardar'); }
  };
  const togglePaid = async () => {
    const next = r.pay === 'paid' ? 'pending' : 'paid';
    const ok = await ask({
      title: next === 'paid' ? 'Marcar la entrada como pagada' : 'Volver a dejar la entrada pendiente de pago',
      details: [r.title, 'La entrada con cóctel se cobra fuera de la plataforma: esto solo deja constancia aquí.'],
      confirmLabel: next === 'paid' ? 'Marcar pagada' : 'Dejar pendiente', tone: 'gold',
    });
    if (!ok) return;
    try { await dashboardApi.adminSetEventRegistrationPayment(r.id, next); onSaved(next === 'paid' ? 'Entrada marcada como pagada' : 'Entrada pendiente de pago'); } catch (e) { toast.error(e.message || 'No se ha podido guardar'); }
  };
  const receipt = async () => {
    try { window.open(await dashboardApi.bootcampReceiptUrl(r.id), '_blank', 'noopener'); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className={card}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-white font-medium">{eventTitle(r.kind, r.title)}</span>
        <span className="text-gray-400">{fdate(r.date)}</span>
        <PayBadge pay={r.pay} label={r.pay_label} />
        {r.kind === 'bootcamp' && r.status_label && <Badge>{r.status_label}</Badge>}
        {showMoney && r.amount !== null && r.amount !== undefined && <span className="text-white tabular-nums">{money(r.amount, r.currency)}</span>}
      </div>
      <FieldList items={[...r.fields, ['Inscrito el', fdatetime(r.created_at)]]} />
      <div className="flex flex-wrap items-end gap-2">
        {r.kind === 'bootcamp' && (
          <label className="block">
            <span className={label}>Estado de la plaza</span>
            <select value={r.status} onChange={(e) => setBootcampStatus(e.target.value)} className={`${inputCls} mt-1 h-10`} aria-label="Estado de la plaza">
              {r.status === 'awaiting_payment' && <option value="awaiting_payment" disabled className="bg-navy-panel">Pendiente de pago</option>}
              {BOOTCAMP_STATUS.map(([k, l]) => <option key={k} value={k} className="bg-navy-panel">{l}</option>)}
            </select>
          </label>
        )}
        {r.can_mark_paid && (
          <button type="button" onClick={togglePaid} className={`${btnGhost} h-10`}>{r.pay === 'paid' ? 'Marcar pendiente' : 'Marcar pagada'}</button>
        )}
        {r.has_receipt && <button type="button" onClick={receipt} className={`${btnGhost} h-10`}>Ver justificante</button>}
      </div>
      {r.kind === 'bootcamp' && (
        <label className="block">
          <span className={label}>Notas del equipo</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} placeholder="Añadir nota"
            className={`${inputCls} mt-1 w-full h-10`} />
        </label>
      )}
    </div>
  );
}
